import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '../../lib/api'

// ==================== Query Keys ====================

const TEMPLATES_KEY = 'checklist-templates'
const ASSIGNMENTS_KEY = 'checklist-assignments'
const INSTANCES_KEY = 'checklist-instances'

// ==================== Types ====================

export type ChecklistRequirement = 'none' | 'optional' | 'required'
export type ChecklistPeriodicity = 'daily' | 'once'
export type ChecklistInstanceStatus = 'in_progress' | 'completed' | 'incomplete'
export type ChecklistItemStatus = 'pending' | 'done' | 'not_done'

export interface ChecklistTemplateItem {
  id: string
  template_id: string
  title: string
  title_kk?: string | null
  description?: string | null
  photo_requirement: ChecklistRequirement
  comment_requirement: ChecklistRequirement
  sort_order: number
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface ChecklistTemplate {
  id: string
  tenant_id: string
  name: string
  name_kk?: string | null
  description?: string | null
  description_kk?: string | null
  is_active: boolean
  created_by?: string | null
  created_at: string
  updated_at: string
  items_count?: number
  assignments_count?: number
  checklist_template_items?: ChecklistTemplateItem[]
  _count?: { checklist_assignments?: number }
}

export interface TemplateFilters {
  search?: string
  is_active?: string
  page?: number
  limit?: number
  sort?: string
}

export interface CreateTemplateItemInput {
  title: string
  description?: string
  photo_requirement?: ChecklistRequirement
  comment_requirement?: ChecklistRequirement
  sort_order?: number
}

export interface CreateTemplateInput {
  name: string
  description?: string
  items?: CreateTemplateItemInput[]
}

export interface ChecklistAssignment {
  id: string
  tenant_id: string
  template_id: string
  department_id?: string | null
  position_id?: string | null
  employee_id?: string | null
  periodicity: ChecklistPeriodicity
  scheduled_date?: string | null
  is_active: boolean
  created_by?: string | null
  created_at: string
  updated_at: string
  checklist_templates?: { id: string; name: string; name_kk?: string | null; is_active: boolean }
  departments?: { id: string; name: string }
  positions?: { id: string; name: string }
  employees?: { id: string; first_name: string; last_name: string; middle_name?: string | null }
}

export interface AssignmentFilters {
  template_id?: string
  department_id?: string
  position_id?: string
  employee_id?: string
  periodicity?: string
  is_active?: string
  page?: number
  limit?: number
  sort?: string
}

export interface CreateAssignmentInput {
  template_id: string
  department_id?: string
  position_id?: string
  employee_id?: string
  periodicity: ChecklistPeriodicity
  scheduled_date?: string
}

export interface ChecklistInstanceItem {
  id: string
  instance_id: string
  template_item_id?: string | null
  title: string
  title_kk?: string | null
  description?: string | null
  photo_requirement: ChecklistRequirement
  comment_requirement: ChecklistRequirement
  sort_order: number
  status: ChecklistItemStatus
  completed_at?: string | null
  photo_url?: string | null
  comment?: string | null
}

export interface ChecklistInstance {
  id: string
  tenant_id: string
  assignment_id: string
  template_id: string
  template_name: string
  template_name_kk?: string | null
  employee_id: string
  department_id?: string | null
  position_id?: string | null
  work_date: string
  issued_at: string
  expected_close_at: string
  status: ChecklistInstanceStatus
  total_items: number
  completed_items: number
  completed_at?: string | null
  closed_at?: string | null
  employees?: { id: string; first_name: string; last_name: string; middle_name?: string | null }
  departments?: { id: string; name: string }
  positions?: { id: string; name: string }
  checklist_instance_items?: ChecklistInstanceItem[]
}

export interface InstanceFilters {
  date_from?: string
  date_to?: string
  department_id?: string
  position_id?: string
  employee_id?: string
  template_id?: string
  status?: string
  page?: number
  limit?: number
  sort?: string
}

// ==================== Templates Hooks ====================

export const useChecklistTemplates = (filters: TemplateFilters = {}) => {
  return useQuery({
    queryKey: [TEMPLATES_KEY, filters],
    queryFn: async () => {
      const response = await apiClient.getChecklistTemplates(filters)
      return {
        data: (response.data || []) as ChecklistTemplate[],
        total: response.total || 0,
        page: response.page || 1,
        limit: response.limit || 20,
        totalPages: response.totalPages || 1,
      }
    },
    staleTime: 30 * 1000,
  })
}

export const useChecklistTemplate = (id: string) => {
  return useQuery({
    queryKey: [TEMPLATES_KEY, id],
    queryFn: async () => {
      const response = await apiClient.getChecklistTemplate(id)
      return response as ChecklistTemplate
    },
    enabled: !!id,
    staleTime: 30 * 1000,
  })
}

export const useCreateChecklistTemplate = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: CreateTemplateInput) => {
      return apiClient.createChecklistTemplate(data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TEMPLATES_KEY] })
    },
  })
}

export const useUpdateChecklistTemplate = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<ChecklistTemplate> }) => {
      return apiClient.updateChecklistTemplate(id, data)
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: [TEMPLATES_KEY] })
      queryClient.invalidateQueries({ queryKey: [TEMPLATES_KEY, id] })
    },
  })
}

export const useDeleteChecklistTemplate = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      return apiClient.deleteChecklistTemplate(id)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TEMPLATES_KEY] })
      // Деактивация шаблона гасит и его назначения
      queryClient.invalidateQueries({ queryKey: [ASSIGNMENTS_KEY] })
    },
  })
}

// ==================== Template Items Hooks ====================

export const useCreateChecklistTemplateItem = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ templateId, data }: { templateId: string; data: CreateTemplateItemInput }) => {
      return apiClient.createChecklistTemplateItem(templateId, data)
    },
    onSuccess: (_, { templateId }) => {
      queryClient.invalidateQueries({ queryKey: [TEMPLATES_KEY, templateId] })
      queryClient.invalidateQueries({ queryKey: [TEMPLATES_KEY] })
    },
  })
}

export const useUpdateChecklistTemplateItem = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      templateId,
      itemId,
      data,
    }: {
      templateId: string
      itemId: string
      data: Partial<ChecklistTemplateItem>
    }) => {
      return apiClient.updateChecklistTemplateItem(templateId, itemId, data)
    },
    onSuccess: (_, { templateId }) => {
      queryClient.invalidateQueries({ queryKey: [TEMPLATES_KEY, templateId] })
      queryClient.invalidateQueries({ queryKey: [TEMPLATES_KEY] })
    },
  })
}

export const useDeleteChecklistTemplateItem = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ templateId, itemId }: { templateId: string; itemId: string }) => {
      return apiClient.deleteChecklistTemplateItem(templateId, itemId)
    },
    onSuccess: (_, { templateId }) => {
      queryClient.invalidateQueries({ queryKey: [TEMPLATES_KEY, templateId] })
      queryClient.invalidateQueries({ queryKey: [TEMPLATES_KEY] })
    },
  })
}

export const useReorderChecklistTemplateItems = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ templateId, itemIds }: { templateId: string; itemIds: string[] }) => {
      return apiClient.reorderChecklistTemplateItems(templateId, itemIds)
    },
    onSuccess: (_, { templateId }) => {
      queryClient.invalidateQueries({ queryKey: [TEMPLATES_KEY, templateId] })
    },
  })
}

// ==================== Assignments Hooks ====================

export const useChecklistAssignments = (filters: AssignmentFilters = {}) => {
  return useQuery({
    queryKey: [ASSIGNMENTS_KEY, filters],
    queryFn: async () => {
      const response = await apiClient.getChecklistAssignments(filters)
      return {
        data: (response.data || []) as ChecklistAssignment[],
        total: response.total || 0,
        page: response.page || 1,
        limit: response.limit || 20,
        totalPages: response.totalPages || 1,
      }
    },
    staleTime: 30 * 1000,
  })
}

export const useCreateChecklistAssignment = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: CreateAssignmentInput) => {
      return apiClient.createChecklistAssignment(data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ASSIGNMENTS_KEY] })
      queryClient.invalidateQueries({ queryKey: [TEMPLATES_KEY] })
    },
  })
}

export const useUpdateChecklistAssignment = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string
      data: { is_active?: boolean; scheduled_date?: string }
    }) => {
      return apiClient.updateChecklistAssignment(id, data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ASSIGNMENTS_KEY] })
    },
  })
}

export const useDeleteChecklistAssignment = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      return apiClient.deleteChecklistAssignment(id)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ASSIGNMENTS_KEY] })
      queryClient.invalidateQueries({ queryKey: [TEMPLATES_KEY] })
    },
  })
}

// ==================== Instances Hooks ====================

export const useChecklistInstances = (filters: InstanceFilters = {}) => {
  return useQuery({
    queryKey: [INSTANCES_KEY, filters],
    queryFn: async () => {
      const response = await apiClient.getChecklistInstances(filters)
      return {
        data: (response.data || []) as ChecklistInstance[],
        total: response.total || 0,
        page: response.page || 1,
        limit: response.limit || 20,
        totalPages: response.totalPages || 1,
      }
    },
    staleTime: 30 * 1000,
  })
}

export const useChecklistInstance = (id: string) => {
  return useQuery({
    queryKey: [INSTANCES_KEY, id],
    queryFn: async () => {
      const response = await apiClient.getChecklistInstance(id)
      return response as ChecklistInstance
    },
    enabled: !!id,
    staleTime: 30 * 1000,
  })
}
